import { action, computed, observable, runInAction } from "mobx";
import { LoaderStore } from "../common";
import { PushNotificationsHandler } from "@utils/specific/capacitor/push-notifications/PushNotificationsHandler";
import { PushNotificationsChannelSubscriptionsApiService } from "@api-services/push-notifications/PushNotificationsChannelSubscriptionsApiService";
import { localizationService } from "@state/services";
import { GroupedSettings } from "@data-types/notification-settings/NotificationSettingsTypes";
import {
	createNotificationSettingsDataForSubmit,
	getGroupItemsAbrvWithDiffValueFromGroup,
	getNotificationGroupsWithItems,
	getNotificationItems,
	sortNotificationGroupsAndItems,
	updateNotificationListFromGroup,
	updateNotificationListFromItem,
} from "@utils/notification-settings/NotificationSettingsMappers";

export class NotificationSettingsStore {
	readonly throttle = 700; //ms
	readonly enableThrottling = true;

	private _updateBuffer = new Set<string>();

	private _bufferTimeoutId: NodeJS.Timeout;

	loaderStore: LoaderStore;

	@observable formData: GroupedSettings[] | null = null;

	@observable permissionStatus: string | null = null;

	@observable listFetchingError = false;

	@computed get isLoading() {
		return this.loaderStore.isLoading;
	}

	@computed get shouldDisableToggleButtons() {
		return this.loaderStore.isLoadingProcess;
	}

	get groupSortOrder() {
		return ["Betslip_Buzz", "Event_Buzz"];
	}

	constructor() {
		this.loaderStore = new LoaderStore();
	}

	@action.bound
	public async onInitialize() {
		await this.checkPermissions();
		await this.fetchNotificationChannels();
	}

	@action.bound
	public onAppResume() {
		if (
			window.location.pathname.includes(
				"account-settings/notification-settings"
			)
		) {
			this.checkPermissions();
		}
	}

	@action.bound
	public onTokenChange() {
		if (
			window.location.pathname.includes(
				"account-settings/notification-settings"
			)
		) {
			this.onInitialize();
		}
	}

	@action.bound
	public updateItem(itemAbrv: string) {
		if (!this.formData) {
			throw new Error("Expected formData here, got undefined");
		}

		this.formData = updateNotificationListFromItem(itemAbrv, this.formData);

		this.toggleNotificationSettingItem(itemAbrv);
	}

	@action.bound
	public updateGroup(groupAbrv: string, newGroupValue: string) {
		if (!this.formData) {
			throw new Error("Expected formData here, got undefined");
		}

		const fieldsToUpdate = getGroupItemsAbrvWithDiffValueFromGroup(
			groupAbrv,
			newGroupValue,
			this.formData
		);

		this.formData = updateNotificationListFromGroup(
			groupAbrv,
			newGroupValue,
			this.formData
		);

		fieldsToUpdate.forEach((field) => {
			this.toggleNotificationSettingItem(field);
		});
	}

	@action.bound
	private async toggleNotificationSettingItem(itemAbrv: string) {
		if (this._updateBuffer.has(itemAbrv)) {
			this._updateBuffer.delete(itemAbrv);
		} else {
			this._updateBuffer.add(itemAbrv);
		}

		this.bufferNotificationSettingItems();
	}

	@action.bound
	private async bufferNotificationSettingItems() {
		if (!this.enableThrottling) {
			await this.handleBuffer();
			return;
		}

		clearTimeout(this._bufferTimeoutId);

		this._bufferTimeoutId = setTimeout(() => {
			this.handleBuffer();
		}, this.throttle);
	}

	@action.bound
	private async handleBuffer() {
		const hasUpdates = !this.isBufferEmpty(this._updateBuffer);

		if (!hasUpdates) {
			//nothing to update
			return;
		}

		if (
			window.location.pathname.includes(
				"account-settings/notification-settings"
			)
		) {
			this.loaderStore.suspend();
		}

		try {
			const updatePayload =
				hasUpdates && this.createValidPayload(this._updateBuffer);

			this.clearUpdateBuffer();

			if (hasUpdates && updatePayload && updatePayload.length > 0) {
				await PushNotificationsChannelSubscriptionsApiService.updateUserPushChannel(
					updatePayload
				);
			}

			if (this.isBufferEmpty(this._updateBuffer)) {
				this.fetchNotificationChannels();
			} else {
				this.handleBuffer();
			}
		} catch (error) {
			console.error(error);
			App.state.rootStore.notificationStore.error(
				localizationService.t(
					"notification-settings:NOTIFICATION_UPDATE_ERROR"
				)
			);
			this.fetchNotificationChannels();
		}
	}

	private isBufferEmpty(buffer: Set<string>) {
		return buffer.size < 1;
	}

	private createValidPayload(buffer: Set<string>) {
		if (!this.formData) {
			return [];
		}

		return createNotificationSettingsDataForSubmit(this.formData, buffer);
	}

	private clearUpdateBuffer() {
		this._updateBuffer = new Set<string>();
	}

	@action.bound
	private async checkPermissions() {
		const permission = await PushNotificationsHandler.getPermissionStatus();
		if (permission != this.permissionStatus) {
			runInAction(() => {
				this.permissionStatus = permission;
			});
		}
	}

	@action.bound
	private async fetchNotificationChannels() {
		this.loaderStore.suspend();
		try {
			const data =
				await PushNotificationsChannelSubscriptionsApiService.fetchPushChannelsPerUser();
			const mappedNotificationItems = getNotificationItems(data);
			const groupedNotificationItems = getNotificationGroupsWithItems(
				mappedNotificationItems
			);
			const sortedGroupedNotificationList =
				sortNotificationGroupsAndItems(
					groupedNotificationItems,
					this.groupSortOrder
				);

			runInAction(() => {
				this.formData = sortedGroupedNotificationList;
			});
		} catch (error) {
			console.error(error);
			runInAction(() => {
				this.listFetchingError = true;
			});
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	public onDispose() {
		this.permissionStatus = null;
		this.listFetchingError = false;
		this.loaderStore.resume();
	}
}
